<script setup lang="ts">
import { computed, toRefs } from "vue";

import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { ChevronDownIcon, InfoIcon } from "~/icons";
import { DropdownMenuItemKind, Entitlement, Placement, Subscription, UserRole } from "~/shared/enums";
import type { User } from "~/shared/types";
import { useDataStore, useTenantStore, useUserStore } from "~/stores";
import { useUpgrade } from "~/utils/billing";

const props = defineProps<{
  row: User;
}>();
const { row } = toRefs(props);

const dataStore = useDataStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();
const { getUpgradeDescription } = useUpgrade(tenantStore);

const userIsAdmin = computed(() => userStore.isRoleGte(UserRole.ADMIN));
const isAtUserLimit = computed(() => {
  const userLimit = tenantStore.getEntitlementValue(Entitlement.MAX_USERS) as number;
  return tenantStore.billedUserCount >= userLimit;
});
const upgradeDisabledForGuest = computed(() => isAtUserLimit.value && row.value.role === UserRole.GUEST);
const roleDropdownDisabled = computed(
  () =>
    !userIsAdmin.value ||
    !tenantStore.isSubscriptionGte(Subscription.APP_SUMO) ||
    row.value.duid === userStore.duid ||
    upgradeDisabledForGuest.value
);
const upgradeTooltipText = computed(() => {
  if (!upgradeDisabledForGuest.value) {
    return undefined;
  }
  return `You have reached your user limit. ${getUpgradeDescription("change a guest to a different role", Subscription.APP_SUMO)}`;
});

const generateRoleDropdownSections = (user: User) => [
  {
    title: "Role",
    items: Object.values(UserRole).map((role) => ({
      title: role,
      disabled: user.role === role,
      kind: DropdownMenuItemKind.BUTTON,
      onClick: () => {
        dataStore.updateUser({ duid: user.duid, role });
      },
    })),
  },
];
</script>

<template>
  <div class="flex items-center gap-1.5">
    <DropdownMenu :disabled="roleDropdownDisabled" :sections="generateRoleDropdownSections(row)" :width-pixels="140">
      <div
        class="flex items-center gap-1.5 rounded px-2 py-1"
        :class="roleDropdownDisabled ? 'cursor-default' : 'hover:bg-lt'">
        <span class="select-none text-sm text-md">
          {{ row.role }}
        </span>
        <ChevronDownIcon v-if="!roleDropdownDisabled" class="text-lt icon-xs" />
      </div>
    </DropdownMenu>
    <Tooltip
      v-if="upgradeDisabledForGuest"
      :text="upgradeTooltipText"
      :placement="Placement.RIGHT_TOP"
      info
      :skidding="-10">
      <InfoIcon class="cursor-help rounded-full outline-none text-vlt icon-xs" />
    </Tooltip>
  </div>
</template>
